import { useEffect, useState, useRef } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import AdminReferralProgramPanel from "../../components/AdminReferralProgramPanel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Settings, Globe, Loader2, Check, Building2, Upload, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("");

  // Business info state
  const [bizInfo, setBizInfo] = useState({
    business_name: "", pan: "", cin: "", address: "",
    telephone: "", email: "", website: "", logo_url: "", home_logo_url: "",
  });
  const [bizLoading, setBizLoading] = useState(true);
  const [bizSaving, setBizSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [homeLogoUploading, setHomeLogoUploading] = useState(false);
  const homeLogoInputRef = useRef(null);
  const [regEnabled, setRegEnabled] = useState(true);
  const [regLoading, setRegLoading] = useState(true);

  // Mobile app version gating
  const [versionForm, setVersionForm] = useState({
    min_version: "",
    latest_version: "",
    play_store_url: "",
    update_message: "",
  });
  const [versionLoading, setVersionLoading] = useState(true);
  const [versionSaving, setVersionSaving] = useState(false);

  // Global Minimum Order Value (for users without segment)
  const [globalMov, setGlobalMov] = useState(0);
  const [globalMovPickup, setGlobalMovPickup] = useState("");
  const [movLoading, setMovLoading] = useState(true);
  const [movSaving, setMovSaving] = useState(false);

  // Global Packaging & Handling % (for users without segment)
  const [globalPack, setGlobalPack] = useState(5);
  const [packLoading, setPackLoading] = useState(true);
  const [packSaving, setPackSaving] = useState(false);

  // Cart Promo Banner (ITPL Club)
  const [promo, setPromo] = useState({
    enabled: true, club_label: "", tagline: "", headline: "", subline: "", footer_text: "",
  });
  const [promoLoading, setPromoLoading] = useState(true);
  const [promoSaving, setPromoSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchCurrencies();
    fetchBusinessInfo();
    fetchRegistration();
    fetchAppVersion();
    fetchGlobalMov();
    fetchGlobalPack();
    fetchPromoBanner();
  }, []);

  const fetchPromoBanner = async () => {
    try {
      const r = await fetch(`${API}/settings/cart-promo-banner`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPromo({
          enabled: !!d.enabled,
          club_label: d.club_label || "",
          tagline: d.tagline || "",
          headline: d.headline || "",
          subline: d.subline || "",
          footer_text: d.footer_text || "",
        });
      }
    } catch (e) { console.error(e); }
    finally { setPromoLoading(false); }
  };

  const savePromoBanner = async () => {
    setPromoSaving(true);
    try {
      const r = await fetch(`${API}/settings/cart-promo-banner`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(promo),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Promo banner saved");
    } catch (e) { toast.error(e.message || "Failed to save"); }
    finally { setPromoSaving(false); }
  };

  const fetchGlobalMov = async () => {
    try {
      const r = await fetch(`${API}/settings/min-order-value`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setGlobalMov(Number(data.min_order_value || 0));
        // Pickup may equal delivery (when unset). Empty input = "fall back to delivery".
        // We detect that by comparing — keep input blank when the two values are
        // identical to signal "not explicitly configured".
        const delivery = Number(data.min_order_value || 0);
        const pickup = Number(data.min_order_value_pickup || 0);
        setGlobalMovPickup(pickup === delivery ? "" : pickup);
      }
    } catch (e) { console.error(e); }
    finally { setMovLoading(false); }
  };

  const saveGlobalMov = async () => {
    setMovSaving(true);
    try {
      const r = await fetch(`${API}/settings/min-order-value`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          min_order_value: Number(globalMov) || 0,
          // Send empty string when the admin cleared the input — backend stores
          // null which means "fall back to delivery threshold at checkout".
          min_order_value_pickup: globalMovPickup === "" || globalMovPickup === null
            ? ""
            : Number(globalMovPickup) || 0,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Minimum order values saved");
    } catch (e) {
      toast.error(e.message || "Failed to save");
    } finally {
      setMovSaving(false);
    }
  };

  const fetchGlobalPack = async () => {
    try {
      const r = await fetch(`${API}/settings/packaging`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setGlobalPack(Number(data.packaging_pct ?? 5));
      }
    } catch (e) { console.error(e); }
    finally { setPackLoading(false); }
  };

  const saveGlobalPack = async () => {
    setPackSaving(true);
    try {
      const r = await fetch(`${API}/settings/packaging`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packaging_pct: Number(globalPack) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Packaging & handling rate saved");
    } catch (e) {
      toast.error(e.message || "Failed to save");
    } finally {
      setPackSaving(false);
    }
  };

  const fetchAppVersion = async () => {
    try {
      const r = await fetch(`${API}/app/version`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setVersionForm({
          min_version: data.min_version || "",
          latest_version: data.latest_version || "",
          play_store_url: data.play_store_url || "",
          update_message: data.update_message || "",
        });
      }
    } catch (e) { console.error(e); }
    finally { setVersionLoading(false); }
  };

  const saveAppVersion = async () => {
    setVersionSaving(true);
    try {
      const r = await fetch(`${API}/admin/app/version`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(versionForm),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Mobile app version updated");
    } catch (e) {
      toast.error(e.message || "Failed to update mobile version");
    } finally {
      setVersionSaving(false);
    }
  };

  const fetchRegistration = async () => {
    try {
      const res = await fetch(`${API}/settings/registration`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRegEnabled(data.registration_enabled);
      }
    } catch (e) { console.error(e); }
    finally { setRegLoading(false); }
  };

  const toggleRegistration = async (val) => {
    try {
      const res = await fetch(`${API}/settings/registration`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ registration_enabled: val }),
      });
      if (res.ok) {
        setRegEnabled(val);
        toast.success(val ? "New registrations enabled" : "New registrations disabled");
      }
    } catch (e) { toast.error("Failed to update"); }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setSelectedCurrency(data.currency_code);
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API}/settings/currencies`, { credentials: "include" });
      if (response.ok) {
        setCurrencies(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const response = await fetch(`${API}/settings/business-info`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        if (data && data.settings_id) {
          setBizInfo({
            business_name: data.business_name || "",
            pan: data.pan || "",
            cin: data.cin || "",
            address: data.address || "",
            telephone: data.telephone || "",
            email: data.email || "",
            website: data.website || "",
            logo_url: data.logo_url || "",
            home_logo_url: data.home_logo_url || "",
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch business info:", error);
    } finally {
      setBizLoading(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings/currency`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currency_code: selectedCurrency }),
      });
      if (!response.ok) throw new Error("Failed to save");
      const data = await response.json();
      setSettings(data);
      toast.success("Currency updated successfully!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update currency");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusinessInfo = async () => {
    setBizSaving(true);
    try {
      const response = await fetch(`${API}/settings/business-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_name: bizInfo.business_name,
          pan: bizInfo.pan,
          cin: bizInfo.cin,
          gstin: bizInfo.gstin,
          address: bizInfo.address,
          telephone: bizInfo.telephone,
          email: bizInfo.email,
          website: bizInfo.website,
        }),
      });
      if (!response.ok) throw new Error("Failed to save");
      toast.success("Business info updated!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update business info");
    } finally {
      setBizSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/settings/business-info/logo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) throw new Error("Upload failed");
      const data = await response.json();
      setBizInfo((prev) => ({ ...prev, logo_url: data.logo_url }));
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleHomeLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHomeLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/settings/business-info/home-logo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) throw new Error("Upload failed");
      const data = await response.json();
      setBizInfo((prev) => ({ ...prev, home_logo_url: data.home_logo_url }));
      toast.success("Home page logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload home page logo");
    } finally {
      setHomeLogoUploading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-2xl" data-testid="admin-settings">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">App Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Configure your application settings</p>
        </div>

        {/* Registration Control */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="registration-control">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${regEnabled ? "bg-green-100" : "bg-red-100"}`}>
                <ShieldAlert className={`w-5 h-5 ${regEnabled ? "text-green-600" : "text-red-600"}`} />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">New User Registration</h2>
                <p className="text-gray-500 text-sm">
                  {regEnabled ? "New users can sign up via Google login" : "Only existing users can log in. New sign-ups are blocked."}
                </p>
              </div>
            </div>
            {regLoading ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <Switch checked={regEnabled} onCheckedChange={toggleRegistration} data-testid="registration-toggle" />
            )}
          </div>
        </div>

        {/* Currency Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Currency</h2>
              <p className="text-gray-500 text-sm">Set the operating currency for your app</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Select Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger className="bg-white mt-1" data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-lg">{currency.symbol}</span>
                        <span>{currency.name}</span>
                        <span className="text-gray-400">({currency.code})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {settings && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">Current Currency</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-gray-900">{settings.currency_symbol}</span>
                  <div>
                    <p className="font-semibold text-gray-900">{settings.currency_name}</p>
                    <p className="text-gray-500 text-sm">{settings.currency_code}</p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSaveCurrency}
              disabled={saving || selectedCurrency === settings?.currency_code}
              className="bg-green-500 hover:bg-green-600"
              data-testid="save-currency-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Check className="w-4 h-4 mr-2" />Save Currency</>}
            </Button>
          </div>
        </div>

        {/* Platform Business Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="business-info-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Platform Business Info</h2>
              <p className="text-gray-500 text-sm">Used on recharge invoices and platform branding</p>
            </div>
          </div>

          {bizLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-gray-400 animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              {/* Logo */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Logo</Label>
                <div className="flex items-center gap-4">
                  {bizInfo.logo_url ? (
                    <div className="relative">
                      <img src={bizInfo.logo_url.startsWith("http") ? bizInfo.logo_url : `${BACKEND_URL}${bizInfo.logo_url}`} alt="Logo" className="w-16 h-16 rounded-xl object-contain border border-gray-200 bg-white" />
                      <button onClick={() => setBizInfo(p => ({ ...p, logo_url: "" }))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50">
                      <Building2 size={20} className="text-gray-300" />
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} data-testid="upload-logo-btn">
                    {logoUploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
                    {logoUploading ? "Uploading..." : "Upload Logo"}
                  </Button>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </div>
                <p className="text-[9px] text-gray-500 mt-1">Used on headers, invoices, admin panel</p>
              </div>

              {/* Home Page Logo */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Home Page Logo</Label>
                <div className="flex items-center gap-4">
                  {bizInfo.home_logo_url ? (
                    <div className="relative">
                      <img src={bizInfo.home_logo_url.startsWith("http") ? bizInfo.home_logo_url : `${BACKEND_URL}${bizInfo.home_logo_url}`} alt="Home Logo" className="w-16 h-16 rounded-xl object-contain border border-gray-200 bg-white" />
                      <button onClick={() => setBizInfo(p => ({ ...p, home_logo_url: "" }))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5" data-testid="remove-home-logo-btn"><X size={12} /></button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50">
                      <Building2 size={20} className="text-gray-300" />
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={() => homeLogoInputRef.current?.click()} disabled={homeLogoUploading} data-testid="upload-home-logo-btn">
                    {homeLogoUploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
                    {homeLogoUploading ? "Uploading..." : "Upload Home Logo"}
                  </Button>
                  <input ref={homeLogoInputRef} type="file" accept="image/*" className="hidden" onChange={handleHomeLogoUpload} data-testid="home-logo-input" />
                </div>
                <p className="text-[9px] text-gray-500 mt-1">Displayed on the landing page</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Business Name</Label>
                  <Input value={bizInfo.business_name} onChange={(e) => setBizInfo(p => ({ ...p, business_name: e.target.value }))} placeholder="Super Morsel Pvt. Ltd." data-testid="biz-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">PAN</Label>
                  <Input value={bizInfo.pan} onChange={(e) => setBizInfo(p => ({ ...p, pan: e.target.value.toUpperCase() }))} placeholder="AABCS1234F" data-testid="biz-pan" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">CIN</Label>
                  <Input value={bizInfo.cin} onChange={(e) => setBizInfo(p => ({ ...p, cin: e.target.value.toUpperCase() }))} placeholder="U56101MH2024PTC123456" data-testid="biz-cin" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">GSTIN</Label>
                  <Input value={bizInfo.gstin || ""} onChange={(e) => setBizInfo(p => ({ ...p, gstin: e.target.value.toUpperCase() }))} placeholder="22AABCS1234F1Z5" data-testid="biz-gstin" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Telephone</Label>
                  <Input value={bizInfo.telephone} onChange={(e) => setBizInfo(p => ({ ...p, telephone: e.target.value }))} placeholder="+91 22 1234 5678" data-testid="biz-telephone" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Email</Label>
                  <Input value={bizInfo.email} onChange={(e) => setBizInfo(p => ({ ...p, email: e.target.value }))} placeholder="billing@supermorsel.com" data-testid="biz-email" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Website</Label>
                  <Input value={bizInfo.website} onChange={(e) => setBizInfo(p => ({ ...p, website: e.target.value }))} placeholder="https://supermorsel.com" data-testid="biz-website" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Address</Label>
                <Input value={bizInfo.address} onChange={(e) => setBizInfo(p => ({ ...p, address: e.target.value }))} placeholder="Registered office address" data-testid="biz-address" />
              </div>

              <Button onClick={handleSaveBusinessInfo} disabled={bizSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-biz-info-btn">
                {bizSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Check className="w-4 h-4 mr-2" />Save Business Info</>}
              </Button>
            </div>
          )}
        </div>

        {/* ===== Global Minimum Order Value ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="global-mov-card">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Minimum Order Value</h2>
          <p className="text-xs text-gray-500 mb-5">
            Applied to customers <span className="font-semibold">without any segment</span> assigned.
            Checked against the net cart value (after discount, before tax).
            Set delivery to <span className="font-mono">0</span> to disable both.
            Leave pickup <span className="font-mono">blank</span> to fall back to the delivery threshold.
          </p>
          {movLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-3 max-w-md">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Delivery — Minimum Order Value (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={globalMov}
                    onChange={(e) => setGlobalMov(e.target.value)}
                    placeholder="0"
                    data-testid="global-mov-input"
                  />
                </div>
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Pickup — Minimum Order Value (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={globalMovPickup}
                    onChange={(e) => setGlobalMovPickup(e.target.value)}
                    placeholder="Same as delivery"
                    data-testid="global-mov-pickup-input"
                  />
                </div>
                <Button onClick={saveGlobalMov} disabled={movSaving} className="bg-green-600 hover:bg-green-700" data-testid="global-mov-save-btn">
                  {movSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save</>}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ===== Cart Promo Banner ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="promo-banner-card">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-gray-900">Cart Promo Banner</h2>
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={promo.enabled}
                onChange={(e) => setPromo({ ...promo, enabled: e.target.checked })}
                className="h-4 w-4 rounded text-green-600"
                data-testid="promo-enabled-toggle"
              />
              <span className="font-medium">Show on cart</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-5">
            Promotional message shown just above the Delivery / Pickup toggle on the customer cart. Leave a field blank to hide that line.
          </p>
          {promoLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-3 max-w-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Club Chip Label</Label>
                  <Input
                    value={promo.club_label}
                    onChange={(e) => setPromo({ ...promo, club_label: e.target.value })}
                    placeholder="ITPL Club"
                    data-testid="promo-club-label-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Tagline</Label>
                  <Input
                    value={promo.tagline}
                    onChange={(e) => setPromo({ ...promo, tagline: e.target.value })}
                    placeholder="Exclusive Offer"
                    data-testid="promo-tagline-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Headline</Label>
                <Input
                  value={promo.headline}
                  onChange={(e) => setPromo({ ...promo, headline: e.target.value })}
                  placeholder="Reduced Minimum Order Value"
                  data-testid="promo-headline-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Sub-headline</Label>
                <Input
                  value={promo.subline}
                  onChange={(e) => setPromo({ ...promo, subline: e.target.value })}
                  placeholder="For deliveries inside ITPL Tech Park"
                  data-testid="promo-subline-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Footer Instruction</Label>
                <Input
                  value={promo.footer_text}
                  onChange={(e) => setPromo({ ...promo, footer_text: e.target.value })}
                  placeholder="To avail, contact our outlet at the Food Court, Park Square Mall."
                  data-testid="promo-footer-input"
                />
              </div>
              <div className="pt-1">
                <Button onClick={savePromoBanner} disabled={promoSaving} className="bg-green-600 hover:bg-green-700" data-testid="promo-save-btn">
                  {promoSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save</>}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ===== Global Packaging & Handling ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="global-packaging-card">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Packaging &amp; Handling</h2>
          <p className="text-xs text-gray-500 mb-5">
            Applied on every <span className="font-semibold">delivery</span> order as a % of (subtotal − discount). Tax is computed on the post-surcharge amount.
            This is the <span className="font-semibold">global rate</span> for customers without any segment. Per-segment overrides take precedence (highest wins when a user belongs to multiple segments).
          </p>
          {packLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="flex items-end gap-3 max-w-sm">
              <div className="flex-1">
                <Label className="text-xs text-gray-500 mb-1 block">Packaging &amp; Handling (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={globalPack}
                  onChange={(e) => setGlobalPack(e.target.value)}
                  placeholder="5"
                  data-testid="global-packaging-input"
                />
              </div>
              <Button onClick={saveGlobalPack} disabled={packSaving} className="bg-green-600 hover:bg-green-700" data-testid="global-packaging-save-btn">
                {packSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          )}
        </div>

        {/* ===== Mobile App Version ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mt-6" data-testid="mobile-version-card">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Mobile App Version</h2>
          <p className="text-xs text-gray-500 mb-5">Users on the Android app below <span className="font-semibold">Minimum Version</span> are force-upgraded via a blocking screen.</p>
          {versionLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="space-y-4 max-w-xl">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Minimum Version</Label>
                  <Input
                    value={versionForm.min_version}
                    onChange={(e) => setVersionForm((p) => ({ ...p, min_version: e.target.value }))}
                    placeholder="1.0.0"
                    data-testid="version-min"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Latest Version</Label>
                  <Input
                    value={versionForm.latest_version}
                    onChange={(e) => setVersionForm((p) => ({ ...p, latest_version: e.target.value }))}
                    placeholder="1.0.1"
                    data-testid="version-latest"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Play Store URL</Label>
                <Input
                  value={versionForm.play_store_url}
                  onChange={(e) => setVersionForm((p) => ({ ...p, play_store_url: e.target.value }))}
                  placeholder="https://play.google.com/store/apps/details?id=com.supermorsel.apps"
                  data-testid="version-store-url"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Update Message</Label>
                <Input
                  value={versionForm.update_message}
                  onChange={(e) => setVersionForm((p) => ({ ...p, update_message: e.target.value }))}
                  placeholder="A new version of Super Morsel is available."
                  data-testid="version-message"
                />
              </div>
              <Button onClick={saveAppVersion} disabled={versionSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="version-save-btn">
                {versionSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save Version Config</>}
              </Button>
            </div>
          )}

          <AdminReferralProgramPanel />
        </div>
      </div>
    </AdminLayout>
  );
}
